import { useTranslation } from "react-i18next";
import {
  Badge,
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import React from "react";
import { PlanDto } from "../../../interfaces/plan/plan.dto";

type TProps = PlanDto & {
  onEdit?: () => void;
  onDelete?: () => void;
  onToggleState?: () => void;
};

export const Plan: React.FC<TProps> = ({
  isActive,
  name,
  price,
  currency,
  grade,
  sequenceNumber,
  isWebPaymentProcess,
  isWebListing,
  isTopSelling,
  features,
  descriptionFeatures,
  onEdit,
  onDelete,
  onToggleState,
}) => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";
  const planName = name?.[langKey] || name?.en || "";
  const gradeName = grade?.name?.[langKey] || grade?.name?.en || "";
  const featureCount = features?.length ?? 0;
  const descriptionFeatureCount = descriptionFeatures?.length ?? 0;

  return (
    <Card
      className={`team-box h-100 mb-0 ${!isActive ? "border-danger border-opacity-25" : ""}`}
      style={{
        opacity: isActive ? 1 : 0.72,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <CardBody className="p-3 d-flex flex-column">
        <div className="d-flex align-items-start justify-content-between mb-3">
          <div className="d-flex flex-wrap gap-1">
            <Badge color={isActive ? "success" : "danger"} className="fs-12">
              {isActive ? t("active") : t("inActive")}
            </Badge>
            <Badge color="secondary" className="fs-12">
              #{sequenceNumber ?? 0}
            </Badge>
            {isTopSelling ? (
              <Badge color="warning" className="fs-12">
                {t("topSelling")}
              </Badge>
            ) : null}
          </div>

          <UncontrolledDropdown>
            <DropdownToggle
              tag="a"
              role="button"
              className="text-muted"
              style={{ cursor: "pointer" }}
            >
              <i className="ri-more-2-fill fs-17" />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem
                className="d-flex align-items-center"
                onClick={onEdit}
              >
                <i className="ri-pencil-line me-2 text-muted" />
                {t("edit")}
              </DropdownItem>
              <DropdownItem
                className="d-flex align-items-center"
                onClick={onToggleState}
              >
                <i
                  className={`${
                    isActive
                      ? "ri-forbid-line"
                      : "ri-checkbox-circle-line"
                  } me-2 text-muted`}
                />
                {isActive ? t("deactivate") : t("activate")}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem
                className="d-flex align-items-center text-danger"
                onClick={onDelete}
              >
                <i className="ri-delete-bin-5-line me-2" />
                {t("delete")}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>

        <div className="mb-3">
          <h5 className="mb-1 text-truncate" title={planName}>
            {planName}
          </h5>
          {gradeName ? (
            <p className="text-muted mb-0 fs-13 text-truncate" title={gradeName}>
              <i className="ri-graduation-cap-line me-1 align-middle" />
              {gradeName}
            </p>
          ) : null}
        </div>

        <div className="mb-3">
          <div className="d-flex align-items-baseline gap-1">
            <span
              className="fs-3 fw-semibold lh-1"
              style={{ color: "var(--vz-heading-color)" }}
            >
              {price}
            </span>
            <span className="fs-13 text-muted fw-medium">
              {currency || "KWD"}
            </span>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-1 mb-3">
          <Badge
            color={isWebPaymentProcess ? "primary" : "secondary"}
            className="fs-12"
          >
            <i
              className={`me-1 ${
                isWebPaymentProcess
                  ? "ri-checkbox-circle-fill"
                  : "ri-close-circle-line"
              }`}
            />
            {t("webPaymentProcess")}
          </Badge>
          <Badge
            color={isWebListing ? "info" : "secondary"}
            className="fs-12"
          >
            <i
              className={`me-1 ${
                isWebListing
                  ? "ri-checkbox-circle-fill"
                  : "ri-close-circle-line"
              }`}
            />
            {t("listOnWebHomePage")}
          </Badge>
        </div>

        {(featureCount > 0 || descriptionFeatureCount > 0) && (
          <div className="d-flex flex-wrap gap-3 mt-auto pt-2 border-top text-muted fs-12">
            {featureCount > 0 ? (
              <span>
                <i className="ri-apps-2-line me-1 align-middle" />
                {featureCount} {t("features")}
              </span>
            ) : null}
            {descriptionFeatureCount > 0 ? (
              <span>
                <i className="ri-list-check me-1 align-middle" />
                {descriptionFeatureCount} {t("planDescriptionFeatures")}
              </span>
            ) : null}
          </div>
        )}
      </CardBody>
    </Card>
  );
};
